/**
 * DreGerencialTable — renderiza o DRE Gerencial real (/api/v2/dre, #121e)
 * em ordem escalonada, interleaveando linhas (R e D) com subtotais
 * calculados (Receita Líquida → EBITDA → Lucro Líquido).
 *
 * - Linhas D1-D10 são clicáveis → drill-down pra aba Despesas (#124).
 * - R6 (MtM) mostra "Em breve" porque a fonte (#121f) ainda não existe.
 * - D11 (mútuos) vai num bloco INFORMATIVO separado com badge fora-DRE.
 *
 * O backend devolve as linhas em ordem de seed (R1..R6, D1..D10); a ordem
 * de APRESENTAÇÃO (escalonada) é responsabilidade do frontend — daí o
 * template `DISPLAY_ORDER` abaixo.
 */
import { ChevronRight } from "lucide-react";
import type { DreV2Response, DreLinhaV2, DreCalculadaV2 } from "../../api/types";
import { fmtBRL } from "../../utils/formatters";

/** Linhas D que permitem drill-down (despesas operacionais). D11 é
 * informativo (não dá drill aqui); R* e calculadas são derivadas/receita. */
const DRILLABLE = new Set(["D1", "D2", "D3", "D4", "D5", "D6", "D7", "D8", "D9", "D10"]);

/** Ordem escalonada de apresentação. `line` busca em secoes operacional;
 * `calc` busca em calculadas. Itens ausentes na resposta são pulados
 * (ex.: EBIT e RECEITA_LIQUIDA_AJUSTADA não existem no regime caixa).
 *
 * Exportada como fonte única da ordem de apresentação — o export PDF (#186)
 * reusa o mesmo template pra garantir que tela e PDF nunca divirjam. */
export const DISPLAY_ORDER: Array<{ kind: "line" | "calc"; codigo: string }> = [
  { kind: "line", codigo: "R1" },
  { kind: "line", codigo: "R2" },
  { kind: "calc", codigo: "RECEITA_LIQUIDA" },
  { kind: "line", codigo: "R6" },
  { kind: "calc", codigo: "RECEITA_LIQUIDA_AJUSTADA" },
  { kind: "line", codigo: "D1" },
  { kind: "calc", codigo: "MARGEM_BRUTA" },
  { kind: "line", codigo: "D2" },
  { kind: "line", codigo: "D3" },
  { kind: "line", codigo: "D4" },
  { kind: "line", codigo: "D5" },
  { kind: "line", codigo: "D6" },
  { kind: "line", codigo: "D7" },
  { kind: "calc", codigo: "EBITDA" },
  { kind: "line", codigo: "D8" },
  { kind: "calc", codigo: "EBIT" },
  { kind: "line", codigo: "D9" },
  { kind: "line", codigo: "R3" },
  { kind: "line", codigo: "R4" },
  { kind: "line", codigo: "R5" },
  { kind: "calc", codigo: "LAIR" },
  { kind: "line", codigo: "D10" },
  { kind: "calc", codigo: "LUCRO_LIQUIDO" },
];

/** Calculadas com destaque forte (turquesa). Exportada — o PDF (#186) usa
 * o mesmo critério pra estilizar EBITDA/Lucro Líquido. */
export const CALC_DESTAQUE_FORTE = new Set(["EBITDA", "LUCRO_LIQUIDO"]);

/** Valor com sinal de apresentação: despesas e deduções (R2) viram
 * negativos; demais receitas positivas. O backend devolve magnitude +
 * `sinal`, mas R2 vem com sinal=+1 (a subtração é feita nas fórmulas),
 * então tratamos R2 explicitamente. */
export function displayValor(l: DreLinhaV2): number {
  const negativo = l.codigo === "R2" || l.codigo.startsWith("D");
  return negativo ? -Math.abs(l.valor_atual) : l.valor_atual;
}

interface Props {
  dre: DreV2Response;
  onDrillDown?: (linhaCodigo: string) => void;
}

export function DreGerencialTable({ dre, onDrillDown }: Props) {
  const linhasPorCodigo = new Map<string, DreLinhaV2>();
  for (const secao of dre.secoes) {
    for (const l of secao.linhas) linhasPorCodigo.set(l.codigo, l);
  }
  const calcPorCodigo = new Map<string, DreCalculadaV2>();
  for (const c of dre.calculadas) calcPorCodigo.set(c.codigo, c);

  const informativo = dre.secoes.find((s) => s.nome.startsWith("INFORMATIVO"));

  return (
    <div
      className="bg-indra-azul-surface rounded-xl border border-white/10 overflow-hidden"
      data-testid="dre-gerencial-table"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
        <h3 className="text-white font-semibold text-sm">Demonstração de Resultado</h3>
        <span className="text-[11px] tracking-wider text-white/60">
          cobertura {dre.header.cobertura_classificacao_pct.toFixed(1)}%
        </span>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left px-5 py-3 text-[11px] tracking-wider text-white/60 font-semibold">
              Conta
            </th>
            <th className="text-right px-5 py-3 text-[11px] tracking-wider text-white/60 font-semibold">
              Valor
            </th>
          </tr>
        </thead>
        <tbody>
          {DISPLAY_ORDER.map((item) => {
            if (item.kind === "calc") {
              const c = calcPorCodigo.get(item.codigo);
              if (!c) return null;
              const forte = CALC_DESTAQUE_FORTE.has(c.codigo);
              const negativo = c.valor_atual < 0;
              return (
                <tr
                  key={`calc-${c.codigo}`}
                  className={"border-b border-white/5 " + (forte ? "bg-indra-turquesa-muted" : "bg-white/5")}
                  data-testid={`dre-calc-${c.codigo}`}
                >
                  <td className={"px-5 py-3 text-sm font-bold " + (forte ? "text-indra-turquesa" : "text-white")}>
                    {c.nome}
                  </td>
                  <td
                    className={
                      "px-5 py-3 text-right text-sm font-mono tabular-nums font-bold " +
                      (forte ? "text-indra-turquesa" : negativo ? "text-indra-negativo" : "text-white")
                    }
                    data-testid={`dre-calc-${c.codigo}-valor`}
                  >
                    {fmtBRL(c.valor_atual, { decimals: 0 })}
                  </td>
                </tr>
              );
            }

            const l = linhasPorCodigo.get(item.codigo);
            if (!l) return null;

            // R6 placeholder — fonte #121f ainda não existe.
            if (l.codigo === "R6") {
              return (
                <tr key="line-R6" className="border-b border-white/5" data-testid="dre-linha-R6">
                  <td className="px-5 py-3 text-sm text-white/50" style={{ paddingLeft: "40px" }}>
                    {l.nome}
                  </td>
                  <td
                    className="px-5 py-3 text-right text-sm text-white/40 italic"
                    data-testid="dre-linha-R6-valor"
                    title="Fonte de dados em desenvolvimento (#121f)"
                  >
                    Em breve
                  </td>
                </tr>
              );
            }

            const drillable = DRILLABLE.has(l.codigo) && onDrillDown !== undefined;
            const valor = displayValor(l);
            const negativo = valor < 0;
            return (
              <tr
                key={`line-${l.codigo}`}
                className={
                  "border-b border-white/5 " +
                  (drillable ? "cursor-pointer hover:bg-white/5 transition-colors" : "")
                }
                onClick={drillable ? () => onDrillDown!(l.codigo) : undefined}
                data-testid={`dre-linha-${l.codigo}`}
                title={drillable ? `Ver despesas de ${l.nome}` : undefined}
              >
                <td className="px-5 py-3 text-sm text-white/80" style={{ paddingLeft: "40px" }}>
                  <span className="inline-flex items-center gap-1.5">
                    {l.nome}
                    {drillable && <ChevronRight size={12} className="text-white/30" />}
                  </span>
                </td>
                <td
                  className={
                    "px-5 py-3 text-right text-sm font-mono tabular-nums " +
                    (negativo ? "text-indra-negativo" : "text-white")
                  }
                  data-testid={`dre-linha-${l.codigo}-valor`}
                >
                  {fmtBRL(valor, { decimals: 0 })}
                </td>
              </tr>
            );
          })}

          {informativo && informativo.linhas.length > 0 && (
            <>
              <tr className="border-b border-white/10 bg-indra-azul-deep/40" data-testid="dre-informativo-header">
                <td colSpan={2} className="px-5 py-2 text-[11px] tracking-wider text-white/60 font-semibold">
                  <span className="inline-flex items-center gap-2">
                    Informativo
                    <span className="text-[9px] bg-amber-500/15 text-amber-300 px-1.5 py-0.5 rounded font-bold tracking-wider">
                      fora-DRE
                    </span>
                  </span>
                </td>
              </tr>
              {informativo.linhas.map((l) => {
                const valor = displayValor(l);
                return (
                  <tr key={`inf-${l.codigo}`} className="border-b border-white/5" data-testid={`dre-informativo-${l.codigo}`}>
                    <td className="px-5 py-2.5 text-sm text-white/60" style={{ paddingLeft: "40px" }}>
                      {l.nome}
                    </td>
                    <td className="px-5 py-2.5 text-right text-sm font-mono tabular-nums text-white/60">
                      {fmtBRL(valor, { decimals: 0 })}
                    </td>
                  </tr>
                );
              })}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}
